import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { LeafIcon, RecycleIcon, BatteryChargingIcon } from 'lucide-react';
import { SectionHeader } from '../components/SectionHeader';
const values = [{
  name: 'Respect',
  colour: '#335F91',
  textColour: '#335F91',
  qualities: ['Listening and supporting others', 'Showing consideration', 'Following the rules']
}, {
  name: 'Friendship',
  colour: '#FFBC0A',
  textColour: '#92700C',
  qualities: ['Comradeship', 'Team work', 'Being brilliant']
}, {
  name: 'Determination',
  colour: '#1a1a1a',
  textColour: '#1a1a1a',
  qualities: ['Pushing to achieve your best', 'Strength of will', 'Working hard']
}, {
  name: 'Excellence',
  colour: '#2E7D32',
  textColour: '#2E7D32',
  qualities: ['Doing your best', 'Striving for improvement', 'Achieving your goals']
}, {
  name: 'Courage',
  colour: '#C33149',
  textColour: '#C33149',
  qualities: ['Excellent behaviour', 'Firm resolve', 'Focused attitude']
}, {
  name: 'Equality',
  colour: '#335F91',
  textColour: '#335F91',
  secondaryColours: ['#335F91', '#ffffff', '#C33149'],
  qualities: ['Equal treatment of all', 'Tolerance and understanding', 'Fair play']
}, {
  name: 'Inspiration',
  colour: '#d4d4d4',
  textColour: '#374151',
  qualities: ['Being a positive role model', 'Enjoying sport when you win or lose', 'Using your achievements to lead the way for others']
}];
const sustainabilityPoints = [{
  icon: <LeafIcon className="w-6 h-6" data-id="element-1162" />,
  title: 'Reduced Carbon Footprint',
  description: 'Lower environmental impact through responsible sourcing and local production.'
}, {
  icon: <RecycleIcon className="w-6 h-6" data-id="element-1163" />,
  title: 'Sustainable Materials',
  description: 'Crafted from responsibly sourced wood, a renewable and natural resource.'
}, {
  icon: <BatteryChargingIcon className="w-6 h-6" data-id="element-1164" />,
  title: 'Less Energy Used',
  description: 'Wooden medal production requires significantly less energy than metal alternatives.'
}];
export function MedalsPage() {
  return <main className="bg-white pt-20" data-id="element-1165">
      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-blue pt-28 pb-20 md:pt-36 md:pb-28 min-h-[320px] md:min-h-[400px]" data-id="element-1166">
        <img src="https://cdn.magicpatterns.com/uploads/pTRbE2fvqTGzPDd7dvdT5h/Group1.webp" alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" data-id="element-1167" />
        <div className="absolute inset-0 bg-brand-blue/85" data-id="element-1168"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1169">
          <nav aria-label="Breadcrumb" className="flex text-sm font-medium text-white/80 mb-8" data-id="element-1170">
            <Link to="/" className="hover:text-white transition-colors" data-id="element-1171">
              Home
            </Link>
            <span className="mx-2" data-id="element-1172">/</span>
            <span className="text-white" data-id="element-1173">Medals</span>
          </nav>

          <div className="max-w-3xl" data-id="element-1174">
            <p className="text-sm md:text-base tracking-[0.3em] uppercase text-white/80 mb-4" data-id="element-1175">
              Team Up With Olympians
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-6 leading-tight" data-id="element-1176">
              Medals
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed" data-id="element-1177">
              Celebrating the Olympic and Paralympic values through our award
              programme for young athletes.
            </p>
          </div>
        </div>
      </section>

      {/* Olympic Values Medals intro */}
      <section className="py-24" data-id="element-1178">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1179">
          <SectionHeader eyebrow="The Olympic Way" title="Olympic Values Medals" description="The Olympic Values can play an important part in life on and off the courts. We want to inspire young children to enjoy sport, learn new skills, build new friendships, strive to achieve their goals and grow in confidence. Children can earn these medals by showing these qualities:" data-id="element-1180" />

          {/* Value cards grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-1181">
            {values.map(value => {
            const isEquality = value.name === 'Equality';
            return <div key={value.name} className="bg-white rounded-2xl border border-navy-100 overflow-hidden shadow-sm flex flex-col" data-id="element-1182">
                  {/* Colour accent bar */}
                  {isEquality && value.secondaryColours ? <div className="flex h-2" data-id="element-1183">
                      {value.secondaryColours.map((c, i) => <div key={i} className="flex-1" style={{
                  backgroundColor: c
                }} data-id="element-1184" />)}
                    </div> : <div className="h-2" style={{
                backgroundColor: value.colour
              }} data-id="element-1185" />}

                  <div className="p-6 flex-1 flex flex-col" data-id="element-1186">
                    <h3 className="text-lg font-display font-bold uppercase tracking-wide mb-4" style={{
                  color: value.textColour
                }} data-id="element-1187">
                      {value.name}
                    </h3>

                    <ul className="space-y-2.5 flex-1" data-id="element-1188">
                      {value.qualities.map((quality, i) => <li key={i} className="flex items-start text-navy-600 text-sm leading-relaxed" data-id="element-1189">
                          <span className="w-1.5 h-1.5 rounded-full mt-1.5 mr-2.5 flex-shrink-0" style={{
                      backgroundColor: value.colour
                    }} data-id="element-1190" />
                          {quality}
                        </li>)}
                    </ul>
                  </div>
                </div>;
          })}
          </div>

          {/* Bronze & Silver progression */}
          <div className="mt-12 bg-brand-blue/5 border border-brand-blue/15 rounded-2xl p-8 text-center max-w-3xl mx-auto" data-id="element-1191">
            <h3 className="text-lg md:text-xl font-display font-bold text-navy-900 uppercase mb-3" data-id="element-1192">
              Achieve More — Bronze, Silver &amp; Gold Medals
            </h3>
            <p className="text-navy-600 leading-relaxed" data-id="element-1193">
              Once a child has earned all seven Olympic Values medals, their
              journey doesn't stop there! They can progress to receive a{' '}
              <strong className="text-[#CD7F32]" data-id="element-1194">Bronze Medal</strong>, then a{' '}
              <strong className="text-[#9CA3AF]" data-id="element-1195">Silver Medal</strong>, and
              ultimately a{' '}
              <strong className="text-[#FFD700]" data-id="element-1196">Gold Medal</strong>,
              recognising their continued commitment to the values both on and
              off the court.
            </p>
          </div>
        </div>
      </section>

      {/* Wooden medals / sustainability */}
      <section className="py-24 bg-emerald-900" data-id="element-1197">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1198">
          <div className="text-center mb-16" data-id="element-1199">
            <span className="block text-sm font-bold tracking-widest uppercase mb-3 text-emerald-300" data-id="element-1200">
              Sustainability
            </span>
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold text-white mb-4" data-id="element-1201">
              Sustainable Wooden Medals
            </h3>
            <p className="text-lg text-emerald-200/70 leading-relaxed max-w-2xl mx-auto" data-id="element-1202">
              Our medals are made from sustainably sourced wood, reflecting our
              commitment to the environment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1203">
            {sustainabilityPoints.map(point => <div key={point.title} className="bg-emerald-800/50 border border-emerald-700/30 rounded-2xl p-6 text-center" data-id="element-1204">
                <div className="w-12 h-12 rounded-full bg-emerald-700/50 flex items-center justify-center mx-auto mb-4 text-emerald-300" data-id="element-1205">
                  {point.icon}
                </div>
                <h4 className="text-white font-display font-bold text-base mb-2" data-id="element-1206">
                  {point.title}
                </h4>
                <p className="text-emerald-200/60 text-sm leading-relaxed" data-id="element-1207">
                  {point.description}
                </p>
              </div>)}
          </div>
        </div>
      </section>
    </main>;
}